import React, { useEffect, useState } from 'react';
import { FormattedDate } from 'react-intl';
import CallStatus from './callStatus';
import { combinedSVGIcons } from 's2s-svg-icons-combined';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { colorGrid } from 's2s-themes';
import { getIntlText } from '../js/utilities';
import useCallInfo from "../hooks/callInfo";

const {
  ArrowUpRightIconSVG,
  LocalPhoneRounded,
  PhoneCallbackRounded,
  PhoneIncomingIconSVG,
  PhonePausedRounded,
  SettingsPhoneRounded,
} = combinedSVGIcons;

const CallDetailContainer = styled.div`
  align-items: stretch;
  background-color: ${props => props.isActiveCall ? colorGrid.gray0 : colorGrid.gray1};
  border-bottom: 1px solid ${colorGrid.gray3};
  border-radius: 4px;
  color: ${colorGrid.gray6};
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: space-between;
  outline: none;
  padding: 12px 25px 12px 13px;
`;

const CallDetailSVGArea = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding-top: 9px;
  width: 32px;
`;

const CallerIdArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-around;
  max-width: calc(100% - 32px - 56px); /* subtract svg width and info area width */
`;

const CallerIdNameArea = styled.div`
  color: ${colorGrid.gray8};
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const CallerExt = styled.div`
  color: ${colorGrid.gray6};
  font-family: Open Sans;
  font-size: 14px;
  line-height: 22px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const CallInfoArea = styled.div`
  margin-left: 8px;
`;

const CallStatusArea = styled.div`
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 900;
`;

const TimerMuteArea = styled.div`
  color : #4C5863;
  font-size: 14px;
`;

const CallDetail = (props) => {
  const [callInfo] = useCallInfo();
  const [timer, setTimer] = useState('00:00:00');
  const isMerged = Object.keys(callInfo.mergedCalls).length !== 0;

  let callTimerId;

  useEffect(() => {
    if (!props.isDate) {
      timerFunc();
    }
    return () => { clearInterval(callTimerId); };
  }, []);

  const timerFunc = () => {
    const startTime = props.timestamp;

    callTimerId = setInterval(() => {
      const rpad = (inString, charToPrepend, numToPrepend) => {
        let nReturn = charToPrepend.repeat(numToPrepend).concat(inString);
        return nReturn.substring(nReturn.length - numToPrepend);
      };

      let newTime = Date.now();
      let delta = newTime - startTime;
      let seconds = Math.floor(delta / 1000 % 60);
      seconds = rpad(seconds, '0', 2);

      let minutes = Math.floor(delta / 1000 / 60 % 60);
      minutes = rpad(minutes, '0', 2);
      let hours = Math.floor(delta / 1000 / 60 / 60 % 60
      );
      hours = rpad(hours, '0', 2);
      setTimer(`${hours}:${minutes}:${seconds}`);
    }, 1000);
  };

  const callDetailKeyDown = (e) => {
    switch (e.keyCode) {
      case 32:
        props.cbClick(props.callId);
        break;
      case 13:
        props.cbClick(props.callId);
        break;
      default:
      // do nothing
    }
  };

  const itemClicked = () => {
    props.cbClick(props.callId);
  };
  
  const timerMute = (
    props.isMuted ? (
      <CallStatus
        isHeld={props.isOnHold}
        status={'Muted'}
        showSVG={false}
        statusText={getIntlText(props.intl, 'CALLDETAILS-MUTED', 'Muted')}
        className="muteStatus"
      />
    ) :
      (!props.timestamp ?
        <div /> :
        (props.isDate ?
          (
            <FormattedDate
              value={props.timestamp}
              weekday="short"
              day="numeric"
              month="short"
              year="numeric"
              hour="2-digit"
              minute="2-digit"
              hour12
            />
          ) :
          timer)
      )
  );
  
  const callerNameNbr = !props.callerName ? (props.callerNumber) : props.callerName;
  
  const showExtNbr = !props.callerName ? undefined : props.callerNumber;
  
  const callerIdName = callerNameNbr && callerNameNbr.length > 21 ?
    callerNameNbr.substring(0, 20) + '...' :
    callerNameNbr;
  
  const calleridOverride = props.callCount && props.callCount > 1 ? `${callerIdName} (${props.callCount})` : callerIdName;
  
  const getDirectionSvg = () => {
    switch (props.callStatusText?.toLowerCase()) {
      case 'active':
        return {
          svg: props.isOnHold ? PhonePausedRounded : LocalPhoneRounded,
          fill: props.isOnHold ? '#F08C00' : '#099F24',
          textColor: props.isOnHold ? '#A77E00' : '#07841E'
        };
      case 'dialing':
        return {
          svg: props.direction === 'inbound' ? PhoneCallbackRounded : SettingsPhoneRounded,
          fill: '#7F888F',
          textColor: '#4C5863'
        };
      case 'ringing':
        return {
          svg: PhoneIncomingIconSVG,
          fill: '#001221',
          textColor: '#243991',
        };
      default:
        return {
          svg: LocalPhoneRounded,
          fill: '#7F888F',
          textColor: '#4C5863'
        };
    }
  };
  
  const { svg: RenderSVG, fill: SvgFill, textColor } = getDirectionSvg();
  
  const callStatus = (
    props.showStatus ?
      (
        <CallStatus
          isHeld={props.isOnHold}
          status={props.callStatus}
          statusText={props.isOnHold ? getIntlText(props.intl, 'CALL-STATUS-ONHOLD', "On hold") : getIntlText(props.intl, 'CALL-STATUS-' + props.callStatus.toUpperCase(), props.callStatus)}
          showSVG={false}
          textColor={textColor}
          className="callStatus"
          isActiveCall={callInfo.activeCallId === props.callId}
        />
      ) :
      (<div className="callStatus"/>)
  );
  
  return (
    <CallDetailContainer
      aria-label="call detail"
      className={`callDetailItem ${props.className}`}
      onClick={itemClicked}
      onKeyDown={callDetailKeyDown}
      role="listitem"
      tabIndex="0"
      isSelected={props.isSelected}
      isActiveCall={callInfo.activeCallId === props.callId}
    >
      <CallDetailSVGArea className="svgArea">
        {!isMerged && (
          <RenderSVG
            svgStyle={{
              fill: SvgFill,
              height: '31px',
              width: '31px'
            }}
          />
        )}
        {isMerged && (
          <ArrowUpRightIconSVG
            svgStyle={{
              fill: colorGrid.gray5,
              height: '32px',
              width: '32px'
            }}
          />
        )}
      </CallDetailSVGArea>
      <CallerIdArea className="callerId">
        <CallerIdNameArea className="callerIdNameArea">{calleridOverride}</CallerIdNameArea>
        <CallerExt className="callerExt">{showExtNbr}</CallerExt>
      </CallerIdArea>
      <CallInfoArea className="infoArea">
        <CallStatusArea className="statusArea">{callStatus}</CallStatusArea>
        <TimerMuteArea className="timerMute">{timerMute}</TimerMuteArea>
      </CallInfoArea>
    </CallDetailContainer>
  );
};

CallDetail.propTypes = {
  intl: PropTypes.object,
  callCount: PropTypes.number,
  callerName: PropTypes.string,
  callId: PropTypes.string.isRequired,
  callerNumber: PropTypes.string, //callers number or ext
  callStatus: PropTypes.oneOf(['active', 'dialing', 'failed', 'ringing', '']), //actual status for use in code.
  //callStatus in parent must have toLowercase() at the end of the prop to ensure validation
  //The empty string is to allow no status to be passed when appropriate.
  callStatusText: PropTypes.string, //translated string passed into status
  cbClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  direction: PropTypes.string,
  isDate: PropTypes.bool,
  isMuted: PropTypes.bool,
  isOnHold: PropTypes.bool,
  isSelected: PropTypes.bool,
  showStatus: PropTypes.bool,
  timestamp: PropTypes.number
};

CallDetail.defaultProps = {
  callCount: undefined,
  callerNumber: undefined,
  callerName: undefined,
  callStatus: '',
  className: '',
  direction: undefined,
  isDate: true,
  isMuted: false,
  isOnHold: false,
  isSelected: false,
  showStatus: true,
  timestamp: undefined
};

export default CallDetail;
